import {
  ClearWappRoomHistory,
  DeleteWappRoom,
  GetWappRoomByWappId,
  MuteWappRoom,
  reqClearWappRoomHistory,
  reqDeleteWappRoom,
  reqGetWappRoomByWappId,
  reqMuteWappRoom,
  reqSetWappRoomUnreadMark,
  reqSyncWappRoom,
  SetWappRoomUnreadMark,
  SyncWappRoom,
  WappRoom,
} from '@weblite/ts-action/wapp-room'
import { removeMessagesByRange } from '$logic/message'
import { request, requestNow } from '$setup/ws'
import { nothing } from '$func/utils'
import { setSeenRoomAllMessages } from '$logic/room-seen'
import { updateRoomLastMessage } from '$logic/room-last-message'
import { showChatRemovedSnackbar } from '$comp/snackbars'
import * as roomService from '../room'
import { wappRoomsStorage } from './storage'
import * as sync from './offline-sync'

export const getWappRoom = wappRoomsStorage.get
export const getAllWappRoom = wappRoomsStorage.getAll

export async function getWappRoomByWappId(
  wappId: string,
): Promise<WappRoom | undefined> {
  const res = await request<GetWappRoomByWappId>(
    reqGetWappRoomByWappId({ wappId }),
  )
  return res.payload?.wappRoom
}

export async function clearHistory(roomId: string): Promise<void> {
  const room = await roomService.getRoom(roomId)
  if (!room) return
  const { iid } = room
  const unsetSync = await sync.set(roomId, { firstVisibleIid: iid + 1 })
  await wappRoomsStorage.set(roomId, { firstVisibleIid: iid + 1 })
  await updateRoomLastMessage(roomId)
  removeMessagesByRange(roomId, 1, iid)

  // NOTE: should resolve after changing in frontend for good offline experience. hence request has no await
  request<ClearWappRoomHistory>(reqClearWappRoomHistory({ iid, roomId })).then(
    async res => {
      await wappRoomsStorage.set(roomId, res.payload)
      await unsetSync()
    },
  )
}

export async function deleteWappRoom(
  roomId: string,
  shouldClearHistory: boolean,
  disableSnackbar = false,
): Promise<void> {
  const room = await roomService.getRoom(roomId)
  if (!room) return
  const { iid } = room
  const unsetDeleteRoomSync = await roomService.addToDeletedRooms(roomId, {
    type: 'WAPP_ROOM',
    iid,
  })
  const unsetClearHistorySync = shouldClearHistory
    ? await sync.set(roomId, { firstVisibleIid: iid + 1 })
    : nothing
  await roomService.pruneRoom(roomId)
  if (shouldClearHistory) removeMessagesByRange(roomId, 1, iid)
  if (!disableSnackbar) showChatRemovedSnackbar()

  // NOTE: should resolve after changing in frontend for good offline experience. hence request has no await
  request<DeleteWappRoom>(
    reqDeleteWappRoom({ roomId, iid, clearHistory: shouldClearHistory }),
  ).then(() => {
    unsetDeleteRoomSync()
    unsetClearHistorySync()
  })
}

export async function setWappRoomMute(
  roomId: string,
  muted: boolean,
): Promise<void> {
  const unsetSync = await sync.set(roomId, { muted })
  await wappRoomsStorage.set(roomId, { muted })

  // NOTE: should resolve after changing in frontend for good offline experience, hence request has no await
  request<MuteWappRoom>(reqMuteWappRoom({ roomId, value: muted })).then(
    async res => {
      await wappRoomsStorage.set(roomId, res.payload)
      await unsetSync()
    },
  )
}

export async function setWappRoomUnreadMark(
  roomId: string,
  unreadMark: boolean,
) {
  const wappRoom = await getWappRoom(roomId)
  if (wappRoom.unreadMark === unreadMark) return

  const unsetSync = await sync.set(roomId, { unreadMark })
  await wappRoomsStorage.set(roomId, { unreadMark })

  // NOTE: should resolve after changing in frontend for good offline experience, hence request has no await
  request<SetWappRoomUnreadMark>(
    reqSetWappRoomUnreadMark({ roomId, value: unreadMark }),
  ).then(async res => {
    await wappRoomsStorage.set(roomId, res.payload)
    await unsetSync()
  })
}

export async function markWappRoomAsRead(roomId: string): Promise<void> {
  await setWappRoomUnreadMark(roomId, false)

  await setSeenRoomAllMessages(roomId)
}

export async function syncWappRoom(): Promise<void> {
  const { items, del, release } = await sync.getAll()
  if (!items.length) {
    release()
    return
  }

  try {
    const wappRooms = items.map(item => ({ roomId: item.id, ...item.patch }))
    const res = await requestNow<SyncWappRoom>(reqSyncWappRoom({ wappRooms }))
    if (res.payload) {
      await Promise.all(
        res.payload.wappRooms.map(({ roomId, ...fields }) =>
          wappRoomsStorage.set(roomId, fields),
        ),
      )
      await del()
    }
  } finally {
    release()
  }
}
